import "@/App.css";
import { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { SubscriptionProvider, Paywall, TrialBanner } from "./components/Paywall";
import { Layout } from "./components/Layout";
import { syncEventTypesFromBackend } from "./lib/utils";
import { Home } from "./pages/Home";
import { Login } from "./pages/Login";
import { Signup } from "./pages/Signup";
import { ForgotPassword } from "./pages/ForgotPassword";
import { ResetPassword } from "./pages/ResetPassword";
import { Events } from "./pages/Events";
import { EventDetail } from "./pages/EventDetail";
import { AddEvent } from "./pages/AddEvent";
import { EditEvent } from "./pages/EditEvent";
import { AIDiscover } from "./pages/AIDiscover";
import { Dashboard } from "./pages/Dashboard";
import { Favorites } from "./pages/Favorites";
import { Settings } from "./pages/Settings";
import { Products } from "./pages/Products";
import { Materials } from "./pages/Materials";
import { Profiles } from "./pages/Profiles";
import { Applications } from "./pages/Applications";
import { MediaLibrary } from "./pages/MediaLibrary";
import { Sales } from "./pages/Sales";
import { Expenses } from "./pages/Expenses";
import { Analytics } from "./pages/Analytics";
import { WhatIfPlanner } from "./pages/WhatIfPlanner";
import { BulkImport } from "./pages/BulkImport";
import { Boutiques } from "./pages/Boutiques";
import { Inventory } from "./pages/Inventory";
import { Onboarding } from "./pages/Onboarding";
import { Pricing } from "./pages/Pricing";
import { HelpCenter, TutorialDetail } from "./pages/HelpCenter";
import { AdminDashboard } from "./pages/AdminDashboard";
import { PrivacyPolicy } from "./pages/PrivacyPolicy";
import { TermsOfService } from "./pages/TermsOfService";
import { VendorCommunity } from "./pages/VendorCommunity";
import { VendorProfile } from "./pages/VendorProfile";
import { VendorProfileEdit } from "./pages/VendorProfileEdit";
import { AdminEventRequests } from "./pages/AdminEventRequests";

// Wrapper to check onboarding status
const OnboardingCheck = ({ children }) => {
  const { user } = useAuth();
  const onboardingComplete = localStorage.getItem('onboarding_complete') === 'true';
  
  // If user is logged in and hasn't completed onboarding, redirect
  if (user && !onboardingComplete && !user.home_city && !user.craft_type) {
    return <Navigate to="/onboarding" replace />;
  }
  
  return children;
};

// Wrapper that blocks rendering until AuthContext has hydrated AND the user is logged in.
// Prevents pages from seeing a transient `user=null` during initial mount and redirecting
// to /login when a valid token is already in localStorage.
const RequireAuth = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh] text-[#71717A] text-sm">
        Loading…
      </div>
    );
  }
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  return children;
};

// Protected route with paywall
const ProtectedRoute = ({ children }) => {
  return (
    <RequireAuth>
      <OnboardingCheck>
        <Paywall>
          {children}
        </Paywall>
      </OnboardingCheck>
    </RequireAuth>
  );
};

function App() {
  // Fetch the canonical EVENT_TYPES registry from backend at boot so UI
  // stays in sync with /api/config/event-types. Failures are silent
  // (static defaults in lib/utils.js remain in effect).
  useEffect(() => {
    syncEventTypesFromBackend();
  }, []);

  return (
    <AuthProvider>
      <SubscriptionProvider>
        <BrowserRouter>
          <TrialBanner />
          <Layout>
            <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/login" element={<Login />} />
              <Route path="/signup" element={<Signup />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/pricing" element={<Pricing />} />
              <Route path="/privacy" element={<PrivacyPolicy />} />
              <Route path="/terms" element={<TermsOfService />} />
              <Route path="/onboarding" element={<Onboarding />} />
              <Route path="/events" element={<ProtectedRoute><Events /></ProtectedRoute>} />
              <Route path="/events/:id" element={<ProtectedRoute><EventDetail /></ProtectedRoute>} />
              <Route path="/events/:id/edit" element={<ProtectedRoute><EditEvent /></ProtectedRoute>} />
              <Route path="/add-event" element={<ProtectedRoute><AddEvent /></ProtectedRoute>} />
              <Route path="/ai-discover" element={<ProtectedRoute><AIDiscover /></ProtectedRoute>} />
              <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
              <Route path="/favorites" element={<ProtectedRoute><Favorites /></ProtectedRoute>} />
              <Route path="/settings" element={<Settings />} />
              <Route path="/products" element={<ProtectedRoute><Products /></ProtectedRoute>} />
              <Route path="/materials" element={<ProtectedRoute><Materials /></ProtectedRoute>} />
              <Route path="/profiles" element={<ProtectedRoute><Profiles /></ProtectedRoute>} />
              <Route path="/applications" element={<ProtectedRoute><Applications /></ProtectedRoute>} />
              <Route path="/media" element={<ProtectedRoute><MediaLibrary /></ProtectedRoute>} />
              <Route path="/sales" element={<ProtectedRoute><Sales /></ProtectedRoute>} />
              <Route path="/expenses" element={<ProtectedRoute><Expenses /></ProtectedRoute>} />
              <Route path="/analytics" element={<ProtectedRoute><Analytics /></ProtectedRoute>} />
              <Route path="/what-if" element={<ProtectedRoute><WhatIfPlanner /></ProtectedRoute>} />
              <Route path="/import" element={<ProtectedRoute><BulkImport /></ProtectedRoute>} />
              <Route path="/boutiques" element={<ProtectedRoute><Boutiques /></ProtectedRoute>} />
              <Route path="/inventory" element={<ProtectedRoute><Inventory /></ProtectedRoute>} />
              <Route path="/help" element={<HelpCenter />} />
              <Route path="/help/:id" element={<TutorialDetail />} />
              <Route path="/admin" element={<ProtectedRoute><AdminDashboard /></ProtectedRoute>} />
              <Route path="/admin/event-requests" element={<ProtectedRoute><AdminEventRequests /></ProtectedRoute>} />
              {/* Vendor Community — Public directory + per-vendor profile (no auth needed so links are shareable) */}
              <Route path="/community" element={<VendorCommunity />} />
              <Route path="/v/:slug" element={<VendorProfile />} />
              {/* Vendor Profile Editor — requires auth */}
              <Route path="/profile/edit" element={<RequireAuth><VendorProfileEdit /></RequireAuth>} />
            </Routes>
          </Layout>
        </BrowserRouter>
      </SubscriptionProvider>
    </AuthProvider>
  );
}

export default App;
